import { isPhoneNumber } from '@bybeam/formatting';
import parseAnswer from '@bybeam/platform-lib/answers/parseAnswer';
import assertExhaustive from '@bybeam/platform-lib/utilities/assertExhaustive';
import isNum from '@bybeam/platform-lib/utilities/isNum';
import {
  AnswerExpression,
  ApplicationAnswers,
  BoolOperator,
  BooleanExpression,
  CompareExpression,
  CompareOperator,
  ConditionExpression,
  Expression,
  ExpressionType,
  FormatExpression,
  FormatType,
  ListExpression,
  ListOperator,
  MathExpression,
  MathOperator,
  DateExpression,
  DateOperator,
} from '@bybeam/platform-types';
import { GenericFormData } from '@platform-ui-common/hooks/useForm/index.js';
import { sum } from '@platform-ui-common/utilities/math.js';
import { flatten } from 'flat';

export function evaluate(
  expression: Expression,
  answers: GenericFormData | Partial<ApplicationAnswers>,
) {
  switch (expression.type) {
    case ExpressionType.Answer:
      return answer(expression, answers);
    case ExpressionType.Boolean:
      return bool(expression, answers);
    case ExpressionType.Compare:
      return compare(expression, answers);
    case ExpressionType.Condition:
      return condition(expression, answers);
    case ExpressionType.Format:
      return format(expression, answers);
    case ExpressionType.List:
      return list(expression, answers);
    case ExpressionType.Literal:
      return expression.value;
    case ExpressionType.Math:
      return math(expression, answers);
    case ExpressionType.Date:
      return date(expression, answers);
    default:
      return assertExhaustive(expression);
  }
}

function answer(expression: AnswerExpression, answers: Partial<ApplicationAnswers>) {
  if (expression.key === '.') return answers;
  // don't flatten arrays
  const extracted = flatten(answers, { safe: true })[expression.key];
  if (typeof extracted !== 'string') return extracted;
  return parseAnswer(expression.key, extracted);
}

function bool(expression: BooleanExpression, answers: Partial<ApplicationAnswers>): boolean {
  switch (expression.op) {
    case BoolOperator.And:
      return expression.expressions.every((exp) => evaluate(exp, answers));
    case BoolOperator.Or:
      return expression.expressions.some((exp) => evaluate(exp, answers));
    case BoolOperator.Not:
      return !evaluate(expression.expression, answers);
    default:
      return assertExhaustive(expression);
  }
}

function compare(expression: CompareExpression, answers: Partial<ApplicationAnswers>): boolean {
  const left = evaluate(expression.comparators[0], answers);
  const right = evaluate(expression.comparators[1], answers);
  switch (expression.op) {
    case CompareOperator.Eq:
      return left === right;
    case CompareOperator.Gt:
      return left > right;
    case CompareOperator.Gte:
      return left >= right;
    case CompareOperator.Lt:
      return left < right;
    case CompareOperator.Lte:
      return left <= right;
    case CompareOperator.In:
      return ((left as (string | number | boolean)[]) ?? []).includes(
        right as string | number | boolean,
      );
    default:
      return assertExhaustive(expression.op);
  }
}

function condition(expression: ConditionExpression, answers: Partial<ApplicationAnswers>) {
  const then = expression.conditions.find((c) => evaluate(c.if, answers))?.then ?? expression.else;
  if (then) return evaluate(then, answers);
}

function format(expression: FormatExpression, answers: Partial<ApplicationAnswers>): boolean {
  const value = evaluate(expression.value, answers);
  switch (expression.format) {
    case FormatType.Pattern:
      return !!value && new RegExp(expression.pattern).test(value);
    case FormatType.Phone:
      return !!value && isPhoneNumber(value);
    default:
      return assertExhaustive(expression);
  }
}

function list(expression: ListExpression, answers: Partial<ApplicationAnswers>) {
  const value = evaluate(expression.list, answers) ?? [];
  switch (expression.op) {
    case ListOperator.Length:
      return value.length;
    case ListOperator.Map:
      return value.map((v) => evaluate(expression.mapper, v));
    case ListOperator.Sum:
      return sum(
        value.map((v) => {
          const parsed = parseAnswer('.', v);
          return isNum(v) ? parsed : 0;
        }),
      );
    default:
      return assertExhaustive(expression);
  }
}

function math(expression: MathExpression, answers: Partial<ApplicationAnswers>) {
  const left = evaluate(expression.expressions[0], answers);
  const right = evaluate(expression.expressions[1], answers);
  if (!isNum(left) || !isNum(right)) return 0;

  switch (expression.op) {
    case MathOperator.Add:
      return (parseAnswer('.', left) as number) + (parseAnswer('.', right) as number);
    case MathOperator.Divide:
      if (right === 0) return 0;
      return left / right;
    case MathOperator.Multiply:
      return left * right;
    case MathOperator.Subtract:
      return left - right;
    default:
      return assertExhaustive(expression.op);
  }
}

function toDate(value: unknown): Date | undefined {
  if (value === undefined || value === null) return undefined;
  const d = value instanceof Date ? value : new Date(value as string | number);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function date(expression: DateExpression, answers: Partial<ApplicationAnswers>) {
  switch (expression.op) {
    case DateOperator.DaysInMonth: {
      const dt = toDate(evaluate(expression.date, answers));
      if (!dt) return 0;
      // Day 0 of next month gives the last day of the current month
      return new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
    }
    case DateOperator.DaysRemainingInMonth: {
      const dt = toDate(evaluate(expression.date, answers));
      if (!dt) return 0;
      const daysInMonth = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate();
      const remaining = daysInMonth - dt.getDate();
      return remaining < 0 ? 0 : remaining;
    }
    case DateOperator.DiffInDays: {
      const [aExp, bExp] = expression.dates;
      const a = toDate(evaluate(aExp, answers));
      const b = toDate(evaluate(bExp, answers));
      if (!a || !b) return 0;
      const diffMs = Math.abs(a.getTime() - b.getTime());
      return Math.floor(diffMs / 86_400_000);
    }
    default:
      return assertExhaustive(expression);
  }
}
